import {
  bindThemeParamsCssVars,
  bindViewportCssVars,
  disableVerticalSwipes,
  expandViewport,
  init as initSdk,
  initDataRaw,
  isChangingFullscreen,
  isFullscreen,
  isSwipeBehaviorMounted,
  isThemeParamsMounted,
  isViewportMounted,
  miniAppReady,
  mountMiniApp,
  mountSwipeBehavior,
  mountThemeParams,
  mountViewport,
  requestFullscreen,
  restoreInitData,
} from '@telegram-apps/sdk-react';
import { insideTelegram, safe } from './env';
import { bindSafeArea } from './safe-area';
import { repaintTelegramChrome } from '../lib/color-scheme';

/** Сколько раз и с каким шагом ждать готовности вьюпорта перед запросом полного экрана. */
const ATTEMPTS = 8;
const RETRY_MS = 250;

/**
 * Инициализация Telegram SDK. Всё обёрнуто в safe(): приложение обязано
 * открываться и в обычном браузере, где никакого Telegram-окружения нет.
 */
export function initTelegram(): void {
  if (!insideTelegram()) return;
  if (!safe(() => initSdk())) return;

  // initData нужен для Authorization на каждом запросе — до первого обращения к API.
  safe(() => restoreInitData());

  safe(() => {
    // Цвета шапки можно ставить только после монтирования miniApp.
    if (mountMiniApp.isAvailable()) {
      void Promise.resolve(mountMiniApp()).then(() => repaintTelegramChrome());
    }
  });
  safe(() => miniAppReady());

  // Тема: SDK сам проставляет и обновляет --tg-theme-*, включая смену на лету.
  safe(() => {
    if (!isThemeParamsMounted()) mountThemeParams();
  });
  safe(() => {
    if (bindThemeParamsCssVars.isAvailable()) {
      bindThemeParamsCssVars((key) => `--tg-theme-${kebab(key)}`);
    }
  });

  // Шапку и нижнюю полосу клиента красит lib/color-scheme.ts — в цвет бумаги.

  // Вьюпорт: полная высота + переменные --tg-viewport-*, включая безопасные зоны.
  // Безопасные зоны — сразу и по событиям клиента, не дожидаясь SDK.
  bindSafeArea();
  safe(() => {
    if (!mountViewport.isAvailable()) return;
    void Promise.resolve(mountViewport())
      .then(() => {
        // Из SDK берём только размеры вьюпорта: безопасные зоны ведёт bindSafeArea.
        safe(() => {
          if (bindViewportCssVars.isAvailable()) {
            bindViewportCssVars((key) => (/safe/i.test(key) ? '' : `--tg-viewport-${kebab(key)}`));
          }
        });
        bindSafeArea();
      })
      .catch(() => {});
  });

  // Полный экран (Bot API 8.0). Клиент может ответить на запрос вьюпорта не
  // сразу, поэтому пробуем несколько раз, а не ждём промис монтирования.
  safe(() => {
    let attempts = 0;
    const attempt = (): void => {
      attempts += 1;
      if (!isViewportMounted() || isChangingFullscreen()) {
        if (attempts < ATTEMPTS) window.setTimeout(attempt, RETRY_MS);
        return;
      }
      void enterFullscreen();
    };
    attempt();
  });

  // Вертикальный свайп по списку не должен закрывать приложение.
  safe(() => {
    if (mountSwipeBehavior.isAvailable() && !isSwipeBehaviorMounted()) mountSwipeBehavior();
  });
  safe(() => {
    if (disableVerticalSwipes.isAvailable()) disableVerticalSwipes();
  });
}

/**
 * Полноэкранный режим (Bot API 8.0): приложение занимает весь экран, шапка
 * клиента убирается. Если клиент не умеет или отказал — расширяемся до
 * максимума доступной высоты, как раньше.
 *
 * Повторно не запрашиваем, если пользователь сам свернул приложение: это его
 * выбор, навязывать полный экран заново нельзя.
 */
async function enterFullscreen(): Promise<void> {
  if (!isViewportMounted() || isFullscreen() || isChangingFullscreen()) return;

  if (requestFullscreen.isAvailable()) {
    try {
      await requestFullscreen();
      bindSafeArea();
      return;
    } catch {
      // ERR_NOT_SUPPORTED / ERR_FULLSCREEN_FAILED: молча падаем в expand().
    }
  }

  safe(() => {
    if (expandViewport.isAvailable()) expandViewport();
  });
}

/**
 * initDataRaw для заголовка Authorization.
 * Вне Telegram берём заготовленную строку из .env — иначе в деве без бота
 * приложение не смогло бы сходить ни в один эндпоинт.
 */
export function getInitDataRaw(): string | undefined {
  const raw = safe(() => initDataRaw());
  if (raw) return raw;

  const devRaw = import.meta.env.VITE_DEV_INIT_DATA;
  return devRaw && devRaw.length > 0 ? devRaw : undefined;
}

/** themeParamsKey приходит в camelCase, а CSS-переменные Telegram — kebab-case. */
function kebab(key: string): string {
  return key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}